/**
 * A second encryption layer, on top of the E2EE in crypto.ts: everything cached
 * locally in IndexedDB (decrypted messages, conversation keys, the identity keypair)
 * is wrapped again with a device-bound AES-GCM key before being written to disk.
 *
 * The key is generated with `extractable: false` and stored as a CryptoKey object
 * directly in IndexedDB (structured-clone supports this) - it can never be exported
 * as raw bytes, even by this app's own code. This protects against someone reading
 * the browser's IndexedDB backing files directly off disk (which are otherwise
 * plaintext LevelDB files); it does not protect against an attacker who can already
 * execute JavaScript in this origin, which is an inherent limit of any client-side
 * scheme - worth being honest about rather than overselling it.
 */

const VAULT_DB_NAME = "heartbeats-vault";
const STORE = "deviceKey";
const KEY_ID = "key";

function openVaultDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(VAULT_DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let keyPromise: Promise<CryptoKey> | null = null;

async function getDeviceKey(): Promise<CryptoKey> {
  if (keyPromise) return keyPromise;

  keyPromise = (async () => {
    const db = await openVaultDb();

    const existing = await new Promise<CryptoKey | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(KEY_ID);
      req.onsuccess = () => resolve(req.result as CryptoKey | undefined);
      req.onerror = () => reject(req.error);
    });
    if (existing) return existing;

    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(key, KEY_ID);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    return key;
  })();

  return keyPromise;
}

export interface SealedBlob {
  iv: Uint8Array;
  data: ArrayBuffer;
}

export async function sealLocal(value: unknown): Promise<SealedBlob> {
  const key = await getDeviceKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, bytes as BufferSource);
  return { iv, data };
}

export async function openLocal<T>(sealed: SealedBlob): Promise<T> {
  const key = await getDeviceKey();
  const bytes = await crypto.subtle.decrypt({ name: "AES-GCM", iv: sealed.iv as BufferSource }, key, sealed.data);
  return JSON.parse(new TextDecoder().decode(bytes)) as T;
}
